import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GeneralInput, GeneralButton, GeneralSelect } from 'sharedComponents';
import { IconCheckedFilled, IconInfoFilled } from 'svgIcons/MotionPortalIcons';
import colors from 'theme/_colors.scss';
import { apiCallStatus } from 'helpers/constants';
import { MAX_SERIAL_NUMBER_LENGTH } from '../helpers';

const CabinetDriveComponent = ({
    cabinetSerialNumber,
    originalSerialNumber,
    initialSuffix,
    suffixes,
    onValidationStateChange,
    onSerialNumberChange,
    onSuffixChange,
    model,
    actions
}) => {
    const { t: translate } = useTranslation();

    const suffixOptions = useMemo(() => {
        return suffixes?.map(suffix => ({
            id: suffix,
            name: suffix
        })) || [];
    }, [suffixes]);

    const initialSerialNumber = cabinetSerialNumber || '';
    const getInitialSuffix = initialSuffix || (suffixOptions.length > 0 ? suffixOptions[0] : null);

    const [serialNumber, setSerialNumber] = useState(initialSerialNumber);
    const [selectedSuffix, setSelectedSuffix] = useState(getInitialSuffix);
    const [isEdited, setIsEdited] = useState(false);
    const [hasValidationAttempted, setHasValidationAttempted] = useState(false);

    useEffect(() => {
        const validationState = model?.validateSerialNumber;
        if (validationState && (validationState.isValid === true || validationState.isValid === false)) {
            setHasValidationAttempted(true);
            onValidationStateChange(validationState.isValid === true);
        } else {
            onValidationStateChange(false);
        }
    }, [model?.validateSerialNumber?.isValid, onValidationStateChange]);

    // Check if user has made changes (for validation button state)
    useEffect(() => {
        const serialChanged = serialNumber !== initialSerialNumber;
        const suffixChanged = selectedSuffix?.id !== getInitialSuffix?.id;
        const hasChanges = serialChanged || suffixChanged;

        setIsEdited(hasChanges);

    }, [serialNumber, selectedSuffix, initialSerialNumber, getInitialSuffix]);

    const handleSerialNumberChange = (value) => {
        if (value.length >= MAX_SERIAL_NUMBER_LENGTH) {
            return;
        }
        setSerialNumber(value);
        setHasValidationAttempted(false);
        onValidationStateChange(false);
        onSerialNumberChange(value);
        onSuffixChange(selectedSuffix);
        if (actions?.clearValidationState) {
            actions.clearValidationState();
        }
    };

    const handleSuffixChange = (suffix) => {
        setSelectedSuffix(suffix);
        setHasValidationAttempted(false);
        onValidationStateChange(false);
        onSuffixChange(suffix);
        onSerialNumberChange(serialNumber);
        if (actions?.clearValidationState) {
            actions.clearValidationState();
        }
    };

    const handleValidate = () => {
        const trimmedSerialNumber = serialNumber.trim();
        const oldSerialNumber = originalSerialNumber || serialNumber;
        const suffixId = selectedSuffix?.id || null;
        const isMultiModuleDrive = true;

        actions.validateSerialNumber(
            oldSerialNumber,
            trimmedSerialNumber,
            suffixId,
            isMultiModuleDrive
        );
    };

    useEffect(() => {
        return () => {
            actions.clearValidationState();
            onValidationStateChange(false);
        };
    }, [actions]);

    const isValid = model?.validateSerialNumber?.isValid;
    const isLoading = model?.validateSerialNumber?.loadingStatus === apiCallStatus.LOADING;

    return (
        <div className='assign__serialnumber__input-container'>
            <form>
                <div id='assign__serialnumber__label'>
                    {translate('ABB.Powertrain.Frontend.assignSerialNumberLabel')}
                </div>
                <div className='assign__serialnumber__input-wrapper'>
                    <div className='assign__serialnumber__input-wrapper__inner'>
                        <div className='assign__serialnumber__input-wrapper__inner-container'>
                            <div className='assign__serialnumber__input-wrapper__inner__cabinet'>
                                <GeneralInput
                                    id='assign__serialnumber__cabinet'
                                    className={`${isValid === false ? 'assign__serialnumber__input--cabinet-error' : 'assign__serialnumber__input--cabinet'} assign__serialnumber__input`}
                                    placeholder={translate('ABB.Powertrain.Frontend.assignSerialNumberPlaceholderText')}
                                    onChange={handleSerialNumberChange}
                                    value={serialNumber}
                                />

                                {/* Success message */}
                                {isValid === true &&
                                    <div className='assign__serialnumber__success-container'>
                                        <IconCheckedFilled
                                            width={18} y
                                            height={18}
                                            color={colors.jungleGreen}
                                            className='success-icon'
                                        />
                                        <span>{translate('ABB.Powertrain.Frontend.assignSerialNumberValidationSuccess')}</span>
                                    </div>
                                }

                                {/* Error message */}
                                {isValid === false &&
                                    <div className='assign__serialnumber__error-container'>
                                        <IconInfoFilled
                                            width={18}
                                            height={18}
                                            color={colors.pomegranate}
                                            className='error-icon'
                                        />
                                        <span>{translate('ABB.Powertrain.Frontend.assignSerialNumberValidationfailed')}</span>
                                    </div>
                                }
                            </div>

                            <GeneralSelect
                                id='assign__serialnumber__inu'
                                className={`${isValid === false ? 'assign__serialnumber__inu-filter-error' : 'assign__serialnumber__inu-filter'}`}
                                options={suffixOptions}
                                getOptionLabel={option => option.name}
                                getOptionValue={option => option.id}
                                onChange={handleSuffixChange}
                                value={selectedSuffix}
                                menuShouldScrollIntoView={false}
                                isSearchable={false}
                            />
                        </div>
                    </div>

                    <GeneralButton
                        type='normal'
                        text={translate('ABB.Powertrain.Frontend.assignSerialNumberValidationButtonLabel')}
                        disabled={!isEdited || !serialNumber.trim().length || hasValidationAttempted || isLoading || !selectedSuffix}
                        onClick={handleValidate}
                    />
                </div>
            </form>
        </div>
    );
};

CabinetDriveComponent.propTypes = {
    cabinetSerialNumber: PropTypes.string,
    originalSerialNumber: PropTypes.string,
    initialSuffix: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string
    }),
    suffixes: PropTypes.array,
    onValidationStateChange: PropTypes.func.isRequired,
    onSerialNumberChange: PropTypes.func.isRequired,
    onSuffixChange: PropTypes.func.isRequired,
    model: PropTypes.object,
    actions: PropTypes.object
};

export default CabinetDriveComponent;
